import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  isNotFound,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { LanguageProvider } from "../lib/language";
import { isNonCanonicalHost } from "../lib/host";
import { GA_MEASUREMENT_ID, trackOutboundClicks } from "../lib/analytics";
import { SITE_URL, ORG_ID, WEBSITE_ID, SOCIAL_PROFILES, langFromPath } from "../lib/site";

function NotFoundComponent() {
  const isAr = useRouterState({ select: (s) => langFromPath(s.location.pathname) === "ar" });
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {isAr ? "الصفحة غير موجودة" : "Page not found"}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {isAr
            ? "الصفحة التي تبحث عنها غير موجودة أو تم نقلها."
            : "The page you're looking for doesn't exist or has been moved."}
        </p>
        <div className="mt-6">
          <Link
            to={isAr ? "/ar" : "/"}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {isAr ? "العودة إلى الرئيسية" : "Go home"}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Resolved once per SSR request and dehydrated; never refetched on client
  // navigation (the host cannot change without a full page load).
  loader: async () => ({ noindex: await isNonCanonicalHost() }),
  staleTime: Infinity,
  head: ({ loaderData }) => ({
    meta: [
      ...(loaderData?.noindex ? [{ name: "robots", content: "noindex,follow" }] : []),
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "IraqCert — Trusted ISO Certification Services in Iraq" },
      {
        name: "description",
        content:
          "ISO certification preparation and oil & gas vendor registration support in Iraq. The issuing body and scope are confirmed per engagement; ISO 29001 is assessed separately.",
      },
      { name: "author", content: "IraqCert" },
      { property: "og:site_name", content: "IraqCert" },
      { property: "og:title", content: "IraqCert — Trusted ISO Certification Services in Iraq" },
      {
        property: "og:description",
        content:
          "ISO certification preparation and oil & gas vendor registration support in Iraq. The issuing body and scope are confirmed per engagement; ISO 29001 is assessed separately.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "ar_IQ" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#B83A1E" },
    ],
    scripts: [
      // GA4 is limited to production builds on the canonical host. SPA page
      // views rely on enhanced measurement; do not add a second emitter.
      ...(import.meta.env.PROD && !loaderData?.noindex
        ? [
            {
              src: `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
              async: true,
            },
            {
              children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA_MEASUREMENT_ID}');`,
            },
          ]
        : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": ORG_ID,
              name: "IraqCert",
              alternateName: ["Iraq Cert", "عراق سيرت"],
              url: `${SITE_URL}/`,
              // Google requires a raster logo of at least 112x112; an SVG is
              // ignored, which cost this Organization node its logo everywhere.
              logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/logo.png`,
                width: 512,
                height: 512,
              },
              image: `${SITE_URL}/og-image.jpg`,
              ...(SOCIAL_PROFILES.length ? { sameAs: SOCIAL_PROFILES.map((p) => p.href) } : {}),
              slogan: "Elevating Iraqi businesses through global standards.",
              description:
                "ISO certification preparation and oil & gas vendor registration support in Iraq. The issuing body and scope are confirmed per engagement; ISO 29001 is assessed separately.",
              email: "info@iraqcert.com",
              telephone: "+9647874946122",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Erbil",
                addressCountry: "IQ",
              },
              areaServed: [
                { "@type": "Country", name: "Iraq" },
                { "@type": "Place", name: "Kurdistan Region of Iraq" },
              ],
              knowsAbout: [
                "ISO 9001 Quality Management",
                "ISO 14001 Environmental Management",
                "ISO 45001 Occupational Health and Safety",
                "ISO 22000 Food Safety Management",
                "ISO 29001 Petroleum and Natural Gas Quality Management",
                "ISO certification",
                "Oil and gas vendor registration",
                "Approved Vendor List registration",
                "Ministry of Iraqi Oil supplier registration",
                "Tender prequalification",
              ],
              contactPoint: {
                "@type": "ContactPoint",
                contactType: "customer service",
                telephone: "+9647874946122",
                email: "info@iraqcert.com",
                availableLanguage: ["English", "Arabic"],
                areaServed: ["IQ"],
              },
            },
            {
              "@type": "WebSite",
              "@id": WEBSITE_ID,
              url: `${SITE_URL}/`,
              name: "IraqCert",
              publisher: { "@id": ORG_ID },
              inLanguage: ["en", "ar"],
              creator: {
                "@type": "Organization",
                "@id": "https://keybridge.krd/#organization",
                name: "Keybridge",
                alternateName: ["KeyBridge"],
                url: "https://keybridge.krd/",
              },
            },
          ],
        }),
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon.svg",
      },
      {
        rel: "icon",
        type: "image/x-icon",
        sizes: "16x16 32x32 48x48",
        href: "/favicon.ico",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "mask-icon",
        href: "/safari-pinned-tab.svg",
        color: "#B83A1E",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const notFound = useRouterState({
    select: (s) =>
      s.matches.some(
        (match) =>
          match.status === "notFound" ||
          isNotFound(match.error) ||
          // Support the global 404 flag in both the locked and newer router versions.
          ("globalNotFound" in match && match.globalNotFound === true) ||
          ("_notFound" in match && match._notFound === true),
      ),
  });
  const lang = langFromPath(pathname);
  return (
    <html lang={lang === "ar" ? "ar-IQ" : "en"} dir={lang === "ar" ? "rtl" : "ltr"}>
      <head>
        {/* Headings use Inter in English and Tajawal 800 in Arabic. Keep this
            language-specific so the other language's fonts remain on demand. */}
        {!notFound && (
          <link
            rel="preload"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
            href={
              lang === "ar"
                ? "/fonts/tajawal-arabic-af463ee17b3e.woff2"
                : "/fonts/inter-latin-c940764593d0.woff2"
            }
          />
        )}
        {notFound ? (
          <>
            <meta charSet="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>
              {lang === "ar"
                ? "404 — الصفحة غير موجودة | عراق سيرت"
                : "404 — Page not found | IraqCert"}
            </title>
            <meta name="robots" content="noindex,follow" />
            <link rel="stylesheet" href={appCss} />
          </>
        ) : (
          <HeadContent />
        )}
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // Every conversation on this site starts with an outbound tap — there is no
  // contact form. These taps measure intent, not a confirmed conversation.
  useEffect(() => trackOutboundClicks(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
      </LanguageProvider>
    </QueryClientProvider>
  );
}
